// vendor modules
import React, { PureComponent } from "react"
import PropTypes from "prop-types"
// internal modules
import Select from "app/static/frontend/selects/components/Select"
import { API_META_LIMIT } from "selects/containers/PieSelect"

// This is a wrapping class around our base Select component. It renders
// that component with some necessary configuration.
// PieSelect takes a permanentFilter if desired, as well as a filterKey to
// use with the user input.
// loadOptions should be an action creator which takes in a filter object
// and returns a Promise that resolves with the desired objects.
// the configuration for each of our selects should take place in the
// containers folder for this subfeature module
// NOTE: this could potentially be a functional component
export class PieSelect extends PureComponent {
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.filterGroupIsOpen === false && this.props.filterGroupIsOpen === true) {
            this.loadOptions("", [])
        }

        // refetch the options if permanentFilter changes
        if (this.props.shouldReloadOnFilterChange && prevProps.permanentFilter !== this.props.permanentFilter) {
            this.loadOptions("", [])
        }

        if (this.props.value && prevProps.value !== this.props.value) {
            this.loadOptions("", [])
        }
    }

    state = {
        nextOffset: 0,
        offset: 0,
        options: [],
        prevInput: "",
    }

    loadOptions = async (input, loadedOptions) => {
        let offset = this.state.offset
        if (this.props.filterGroupIsOpen === false) {
            // Must return an object with the following properties:
            return {
                options: [],
                hasMore: false,
                additional: null,
            }
        }

        if (this.state.options.length && offset === 0) offset = this.props?.apiMetaLimit ?? API_META_LIMIT

        // if we have loaded any options (we have scrolled at least one offset down),
        // then update the offset accordingly

        if (loadedOptions.length && input === this.state.prevInput) {
            offset = this.state.nextOffset
            this.setState({ offset: this.state.nextOffset })
        }
        // if we search, clear the previously cached offset and start fresh
        else if (input !== this.state.prevInput) {
            // "search" to ""
            if (this.state.prevInput && !input) {
                offset = this.state.defaultOffset
                this.setState({
                    defaultOffset: undefined,
                    offset: this.state.defaultOffset,
                })
                // "" to "search"
            } else if (!this.state.prevInput && input) {
                offset = 0
                this.setState({
                    defaultOffset: this.state.nextOffset,
                    offset: 0,
                })
            }

            // if the input changes, reset the offsets
            offset = 0
            this.setState({
                offset: 0,
                nextOffset: 0,
            })
        }

        // invoke loadOptions from PieSelect container
        const { options, hasMore, nextOffset } = await this.props.loadOptions({
            ...this.props.permanentFilter,
            ...(input ? { [this.props.filterKey]: input } : {}),
            // always make extra request to initialValues if the id of the current value
            // is not contained within the async options array
            initialValues: !this.state.options.some((option) => option.id === this.props.value)
                ? this.props.value
                : undefined,
            offset: offset,
        })

        if (this.props.shouldReloadOnFilterChange) {
            this.setState({
                options: [...options],
                prevInput: input,
                nextOffset,
            })
        } else {
            this.setState({
                options: [...this.state.options, ...options],
                prevInput: input,
                nextOffset,
            })
        }

        return {
            options,
            hasMore,
            additional: null,
        }
    }

    render() {
        return (
            <Select
                {...this.props}
                advancedSearchifyConfig={this.props.advancedSearchifyConfig}
                allowExpandUp={this.props.allowExpandUp}
                label={this.props.label}
                loadOptions={this.loadOptions}
                menuShouldScrollIntoView={this.props.menuShouldScrollIntoView}
                multi={this.props.multi}
                onChange={this.props.onChange}
                options={this.state.options}
                placeholder={this.props.placeholder}
            />
        )
    }
}

PieSelect.defaultProps = {
    allowExpandUp: false,
    filterKey: "quick_search",
    menuShouldScrollIntoView: true,
    multi: false,
    permanentFilter: {},
}
PieSelect.propTypes = {
    allowExpandUp: PropTypes.bool,
    advancedSearchifyConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    filterKey: PropTypes.string,
    // from shared/search/containers/Filter/index.js
    filterGroupIsOpen: PropTypes.bool,
    label: PropTypes.string,
    loadOptions: PropTypes.func,
    menuShouldScrollIntoView: PropTypes.bool,
    multi: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    permanentFilter: PropTypes.object,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([
        // [1, 5, 25]
        PropTypes.array,
        // 1
        PropTypes.number,
    ]),
}

export default PieSelect
